import { uuidv7 } from '@office/domain'

/**
 * The offline outbox (docs/02 §8).
 *
 * Every write the user makes is appended here FIRST and applied to the local
 * mirror immediately, so the UI never waits on the network. The queue is
 * replayed against the server whenever we reconnect.
 *
 * Two rules make replay safe, and both live on the server side too:
 *   - each mutation carries a client-minted UUIDv7 id, so replaying one twice
 *     is a no-op rather than a duplicate;
 *   - a mutation the server REJECTS is terminal. It is removed from the queue
 *     and surfaced to the user. It is never retried, because retrying a
 *     permanently-invalid mutation forever is exactly how a queue wedges and
 *     sync silently stops.
 */
export interface Mutation {
  id: string
  kind: string
  payload: Record<string, unknown>
  /** When it was queued, by the trusted clock. */
  queuedAt: number
  /** Transient-failure attempts so far. Rejections do not increment this. */
  attempts: number
  lastError: string | null
}

export type PushOutcome = 'applied' | 'rejected' | 'duplicate'

export interface PushResult {
  mutation_id: string
  outcome: PushOutcome
  reason: string | null
}

export function makeMutation(
  kind: string, payload: Record<string, unknown>, now: number,
): Mutation {
  return { id: uuidv7(now), kind, payload, queuedAt: now, attempts: 0, lastError: null }
}

/** Rejections the user should be told about, e.g. "that room got booked". */
export interface Rejection {
  mutation: Mutation
  reason: string
  at: number
}

export interface DrainReport {
  applied: string[]
  duplicate: string[]
  rejected: Rejection[]
  /** Still queued: transient failures worth retrying. */
  retained: Mutation[]
}

/**
 * Decide what happens to the queue given the server's per-mutation verdicts.
 * Pure: the caller performs the storage writes. This is the piece worth testing
 * exhaustively, so it is deliberately free of I/O.
 */
export function reconcile(
  queue: Mutation[], results: PushResult[], now: number,
): DrainReport {
  const byId = new Map(results.map(r => [r.mutation_id, r]))
  const report: DrainReport = { applied: [], duplicate: [], rejected: [], retained: [] }

  for (const m of queue) {
    const r = byId.get(m.id)
    if (!r) {
      // No verdict came back for this one: the request was cut short. Keep it —
      // it is idempotent, so re-sending is always safe.
      report.retained.push({ ...m, attempts: m.attempts + 1, lastError: 'no response' })
      continue
    }
    if (r.outcome === 'applied') report.applied.push(m.id)
    else if (r.outcome === 'duplicate') report.duplicate.push(m.id)
    else report.rejected.push({ mutation: m, reason: r.reason ?? 'rejected', at: now })
  }
  return report
}

/** Exponential backoff with a ceiling, so a flapping network is not hammered. */
export function backoffMs(attempts: number): number {
  const base = 1000 * 2 ** Math.min(attempts, 6)
  return Math.min(base, 60_000)
}
